import {
  createParser,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import type { SortDirection } from '../types/Filters';
import {
  PERCENTAGE_CHECK_BUCKETS,
  PERCENTAGE_CHECK_SORT_FIELDS,
  type PercentageCheckBucket,
  type PercentageCheckFilters,
  type PercentageCheckRangeKey,
  type PercentageCheckSortField,
} from '../types/PercentageCheck';
import { parseApiDate, toApiDate } from '../utils/data';
import { SORT_DIRECTIONS } from '../utils/filters';

export const PERCENTAGE_CHECK_MAX_PERIOD_DAYS = 366;

const parseAsLocalDate = createParser({
  parse: (value: string) => parseApiDate(value),
  serialize: (date: Date) => toApiDate(date) ?? '',
  eq: (a: Date, b: Date) => toApiDate(a) === toApiDate(b),
});

const RANGE_PARSERS = {
  maxPercentageFrom: parseAsInteger,
  maxPercentageTo: parseAsInteger,
  minPercentageFrom: parseAsInteger,
  minPercentageTo: parseAsInteger,
  daysBelowFrom: parseAsInteger,
  daysBelowTo: parseAsInteger,
  daysAboveFrom: parseAsInteger,
  daysAboveTo: parseAsInteger,
};

const PARSERS = {
  startDate: parseAsLocalDate,
  endDate: parseAsLocalDate,
  bucket: parseAsStringLiteral(PERCENTAGE_CHECK_BUCKETS).withDefault('all'),
  includeZero: parseAsBoolean.withDefault(true),
  professionalSearch: parseAsString,
  contractTypeIds: parseAsArrayOf(parseAsInteger).withDefault([]),
  contractStatusIds: parseAsArrayOf(parseAsInteger).withDefault([]),
  unitIds: parseAsArrayOf(parseAsInteger).withDefault([]),
  ...RANGE_PARSERS,
  sortBy: parseAsStringLiteral(PERCENTAGE_CHECK_SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(20),
};

type IdListKey = 'unitIds' | 'contractTypeIds' | 'contractStatusIds';

const toBound = (value: number | null): number | null =>
  value === null || Number.isNaN(value) ? null : Math.max(0, Math.round(value));

const listOrUndefined = (ids: number[]) => (ids.length > 0 ? ids : undefined);

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function currentMonth() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
  };
}

export function usePercentageCheckFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const defaults = useMemo(currentMonth, []);
  const startDate = state.startDate ?? defaults.start;
  const endDate = state.endDate ?? defaults.end;

  const filters: PercentageCheckFilters = useMemo(
    () => ({
      startDate: toApiDate(startDate) ?? '',
      endDate: toApiDate(endDate) ?? '',
      bucket: state.bucket,
      professionalSearch: state.professionalSearch ?? undefined,
      contractTypeIds: listOrUndefined(state.contractTypeIds),
      contractStatusIds: listOrUndefined(state.contractStatusIds),
      unitIds: listOrUndefined(state.unitIds),
      maxPercentageFrom: state.maxPercentageFrom ?? undefined,
      maxPercentageTo: state.maxPercentageTo ?? undefined,
      minPercentageFrom: state.minPercentageFrom ?? undefined,
      minPercentageTo: state.minPercentageTo ?? undefined,
      daysBelowFrom: state.daysBelowFrom ?? undefined,
      daysBelowTo: state.daysBelowTo ?? undefined,
      daysAboveFrom: state.daysAboveFrom ?? undefined,
      daysAboveTo: state.daysAboveTo ?? undefined,
      sortBy: state.sortBy ?? undefined,
      sortDirection: state.sortDirection ?? undefined,
      page: state.page,
      perPage: state.perPage,
    }),
    [startDate, endDate, state],
  );

  const setPeriod = useCallback(
    (start: Date | null, end: Date | null) => {
      let nextEnd = end;
      if (start && end) {
        if (end < start) nextEnd = start;
        const limit = addDays(start, PERCENTAGE_CHECK_MAX_PERIOD_DAYS - 1);
        if (end > limit) nextEnd = limit;
      }
      return setState({ startDate: start, endDate: nextEnd, page: 1 });
    },
    [setState],
  );

  const setBucket = useCallback(
    (bucket: PercentageCheckBucket) => setState({ bucket, page: 1 }),
    [setState],
  );

  const setIncludeZero = useCallback(
    (includeZero: boolean) => setState({ includeZero, page: 1 }),
    [setState],
  );

  const setSearch = useCallback(
    (professionalSearch: string | null) =>
      setState({ professionalSearch: professionalSearch?.trim() || null, page: 1 }),
    [setState],
  );

  const setIds = useCallback(
    (key: IdListKey, ids: number[]) => setState({ [key]: ids, page: 1 }),
    [setState],
  );

  const setRange = useCallback(
    (key: PercentageCheckRangeKey, range: { min: number | null; max: number | null }) =>
      setState({ [`${key}From`]: toBound(range.min), [`${key}To`]: toBound(range.max), page: 1 }),
    [setState],
  );

  const applyAdvanced = useCallback(
    (patch: {
      professionalSearch: string;
      unitIds: number[];
      contractTypeIds: number[];
      contractStatusIds: number[];
      includeZero: boolean;
    }) =>
      setState({
        includeZero: patch.includeZero,
        professionalSearch: patch.professionalSearch.trim() || null,
        unitIds: patch.unitIds,
        contractTypeIds: patch.contractTypeIds,
        contractStatusIds: patch.contractStatusIds,
        page: 1,
      }),
    [setState],
  );

  const setSort = useCallback(
    (field: PercentageCheckSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    [setState],
  );

  const goToPage = useCallback((page: number) => setState({ page }), [setState]);
  const setPerPage = useCallback((perPage: number) => setState({ perPage, page: 1 }), [setState]);

  const clearFilters = useCallback(
    () =>
      setState({
        bucket: 'all',
        includeZero: true,
        professionalSearch: null,
        contractTypeIds: [],
        contractStatusIds: [],
        unitIds: [],
        maxPercentageFrom: null,
        maxPercentageTo: null,
        minPercentageFrom: null,
        minPercentageTo: null,
        daysBelowFrom: null,
        daysBelowTo: null,
        daysAboveFrom: null,
        daysAboveTo: null,
        page: 1,
      }),
    [setState],
  );

  const hasActiveFilters =
    state.bucket !== 'all' ||
    !state.includeZero ||
    !!state.professionalSearch ||
    state.contractTypeIds.length > 0 ||
    state.contractStatusIds.length > 0 ||
    state.unitIds.length > 0 ||
    Object.keys(RANGE_PARSERS).some((key) => state[key as keyof typeof RANGE_PARSERS] !== null);

  return {
    filters,
    state: { ...state, startDate, endDate },
    setPeriod,
    setBucket,
    setIncludeZero,
    setSearch,
    setIds,
    setRange,
    applyAdvanced,
    setSort,
    goToPage,
    setPerPage,
    clearFilters,
    hasActiveFilters,
  };
}

export type PercentageCheckFilterState = ReturnType<typeof usePercentageCheckFilters>;
